import { Controller } from "@hotwired/stimulus"
import * as offlineQueue from "../lib/offline_queue"

// Best-effort client-side companion to the Clear-Site-Data response
// header sent by Users::SessionsController#destroy (see
// docs/superpowers/specs/2026-08-06-offline-run-mode-design.md, "Sign-out
// clears offline state"). The header is the actual guarantee — this just
// asks an already-registered service worker to drop its caches a moment
// earlier, before the sign-out request round-trips.
//
// Task 4 obligation (same spec section): sign-out also wipes the offline
// capture queue (IndexedDB is part of "storage" in the header above), so
// signing out with unsynced queued captures loses them — same as an
// unsaved paper form, but silent unless we ask first. So: if the queue is
// non-empty, confirm before letting sign-out proceed.
//
// Kept as its own tiny controller — rather than extending
// offline_controller — so attaching it to the sign-out link (which
// renders in the main application layout, not just run mode) doesn't also
// trigger service worker (re-)registration outside run mode's intended
// offline scope. It reads the same IndexedDB queue directly, though: the
// queue is a browser-origin store, not something scoped to run mode's own
// controller instance, so it's readable from any page.
export default class extends Controller {
  async clearCache(event) {
    // The queue count check below is async (IndexedDB), so the original
    // click's default action (Turbo's turbo-method DELETE) has to be
    // stopped unconditionally, right now — by the time the promise
    // resolves it would already have fired. If we don't end up wanting to
    // block sign-out, submitSignOut() below re-drives it ourselves.
    event.preventDefault()

    const queued = await offlineQueue.count().catch(() => 0)

    if (queued > 0) {
      const noun = queued === 1 ? "capture hasn't" : "captures haven't"
      const proceed = window.confirm(`${queued} ${noun} synced yet — sign out anyway?`)
      if (!proceed) return
    }

    // Once the operator has confirmed (or there was nothing to confirm),
    // wipe the queue itself here too (final review M2) — best-effort, same
    // spirit as the CLEAR_CACHE postMessage below: the Clear-Site-Data
    // response header is still the actual guarantee (see the design doc's
    // "Sign-out clears offline state"), but that only takes effect once
    // the sign-out response round-trips. A failure here must never block
    // sign-out itself — the header behind it doesn't depend on this call
    // having succeeded.
    await offlineQueue.clearAll().catch(() => {})

    navigator.serviceWorker?.controller?.postMessage({ type: "CLEAR_CACHE" })
    this.submitSignOut()
  }

  // Rebuilds the same POST + _method=delete form Turbo's own data-turbo-method
  // handling would have submitted for this link. requestSubmit (not the
  // legacy, event-less form.submit()) dispatches a real `submit` event, so
  // Turbo Drive still intercepts it for a normal Turbo visit.
  submitSignOut() {
    const form = document.createElement("form")
    form.method = "post"
    form.action = this.element.href
    form.hidden = true

    const methodField = document.createElement("input")
    methodField.type = "hidden"
    methodField.name = "_method"
    methodField.value = "delete"
    form.append(methodField)

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (token) {
      const csrfField = document.createElement("input")
      csrfField.type = "hidden"
      csrfField.name = "authenticity_token"
      csrfField.value = token
      form.append(csrfField)
    }

    document.body.append(form)
    form.requestSubmit()
  }
}
